<template lang="pug">
.row
  template(v-if='loaded && integrations.length > 0')
    .col-12.mb-3
      .search-wrapper
        .search-input
          input.form-control(
            :placeholder='$t("integrations.searchPlaceholder")'
            type='text'
            v-model='search'
          )
          .input-icon
            Icon(name='magnifying-glass')
        button.sort-button(@click='toggleSort' type='button')
          Icon(
            :key='sortAsc'
            :name='sortAsc ? "arrow-up-a-z" : "arrow-down-z-a"'
          )

    .d-flex.flex-wrap.align-items-stretch.justify-content-start
      .card-wrapper(
        :key='integration.id'
        v-for='integration in filteredIntegrations'
      )
        IntegrationCard(
          :integration='integration'
          @configure='handleConfigure(integration)'
          @deactivate='handleDeactivate(integration)'
        )

  template(v-else-if='loaded')
    .col-12.text-center.empty-state
      img.empty-illustration(
        :alt='$t("integrations.noActiveIntegrations")'
        src='/img/ilustrations/no-active-integrations.svg'
      )
      p.empty-subtitle
        | {{ $t('integrations.emptyStateTitle') }}
        br
        | {{ $t('integrations.emptyStateSubtitle') }}
      button.btn.btn-success.mt-3(@click='handleAddIntegration')
        | {{ $t('integrations.addNewIntegration') }}

  IntegrationConfigureModal(
    :integration='selectedIntegration'
    @saved='onSaved'
    ref='configureModal'
    v-if='selectedIntegration'
  )

  IntegrationDisconnectModal(
    :integration='selectedIntegrationForDisconnect'
    @disconnected='fetchActiveIntegrations'
    ref='disconnectModal'
    v-if='selectedIntegrationForDisconnect'
  )
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import alert from '@/modules/account/common/alert';
import IntegrationCard from '@/modules/account/components/organisms/card-table/IntegrationCard.vue';
import IntegrationConfigureModal from '@/modules/account/components/organisms/modals/IntegrationConfigureModal.vue';
import IntegrationDisconnectModal from '@/modules/account/components/organisms/modals/IntegrationDisconnectModal.vue';
import IntegrationsService from '@/services/IntegrationsService';

export default {
  name: 'ActiveIntegrations',
  components: {
    Icon,
    IntegrationCard,
    IntegrationConfigureModal,
    IntegrationDisconnectModal,
  },
  data() {
    return {
      search: '',
      sortAsc: true,
      integrations: [],
      loaded: false,
      selectedIntegration: null,
      selectedIntegrationForDisconnect: null,
    };
  },
  computed: {
    sortedIntegrations() {
      return [...this.integrations].sort((a, b) =>
        this.sortAsc
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );
    },
    filteredIntegrations() {
      const term = this.search.toLowerCase();
      return this.sortedIntegrations.filter((i) =>
        i.name.toLowerCase().includes(term)
      );
    },
  },
  methods: {
    async fetchActiveIntegrations() {
      this.loaded = false;
      try {
        await this.$store.dispatch('organizations/getItems');
        const orgId = this.$store.state.organizations.selected.id;
        const resp = await IntegrationsService.listActive(orgId);

        this.integrations = resp.data.data.map((item) => ({
          id: item.value || item.id,
          name: item.label || item.name,
          description: item.description,
          logo: item.icon,
          type: item.value || item.type,
          config: item.config || {},
          active: true,
        }));
      } catch (err) {
        console.error(this.$t('integrations.errorFetchingIntegrations'), err);
      } finally {
        this.loaded = true;
      }
    },
    handleConfigure(integration) {
      this.selectedIntegration = {
        ...integration,
        config: { ...integration.config, password: '' },
      };
      this.$nextTick(() => this.$refs.configureModal.open());
    },

    handleDeactivate(integration) {
      this.selectedIntegrationForDisconnect = integration;
      this.$nextTick(() => {
        this.$refs.disconnectModal.open();
      });
    },

    onSaved({ name }) {
      this.fetchActiveIntegrations();
      this.selectedIntegration = null;
      alert.fireAlert(
        this.$t('integrations.integrationConfiguredSuccess', { name }),
        {
          classes: 'alert-success',
          styles:
            'background-color: #d4edda; border-color: #c3e6cb; color: #155724;',
          icon: 'circle-check',
        }
      );
    },
    toggleSort() {
      this.sortAsc = !this.sortAsc;
    },
    handleAddIntegration() {
      this.$router.push({ name: 'account.integrations.alltools' });
    },
  },
  created() {
    this.$store.dispatch('page/setTitle', this.$t('nav.admin.integrations'));
    this.$store.dispatch('page/setHead', this.$t('nav.admin.integrations'));
    this.$store.dispatch('page/setClass', 'account integrations');
    this.fetchActiveIntegrations();
  },
};
</script>

<style scoped>
.search-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 358px;
  margin-bottom: 16px;
}
.search-input {
  position: relative;
  flex: 1;
}
.search-input .form-control {
  width: 100%;
  padding-right: 2.5rem;
  border-radius: 8px;
  border: 1px solid #ced4da;
  height: 38px;
}
.input-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #495057;
  pointer-events: none;
}
.sort-button {
  border: none;
  background: transparent;
  font-size: 1.25rem;
  color: #495057;
  cursor: pointer;
}
.card-wrapper {
  width: 316px;
  margin: 8px;
}
.empty-state {
  padding: 40px 0;
}
.empty-illustration {
  max-width: 200px;
  margin-bottom: 16px;
}
.empty-subtitle {
  color: #555;
  font-weight: 400;
  margin-bottom: 16px;
}
.empty-state .btn-success {
  background-color: #19665b !important;
  border-color: #19665b !important;
  color: #fff !important;
}
@media (max-width: 992px) {
  .search-wrapper {
    padding-left: 10px;
    max-width: 100%;
  }
}
</style>
